import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useCartStore } from '@/stores/cart.store';
import { useShallow } from 'zustand/react/shallow';

const schema = z.object({
  name: z.string().trim().min(1, 'Nama wajib diisi').max(120, 'Maksimal 120 karakter'),
  phone: z
    .string()
    .trim()
    .max(20, 'Maksimal 20 digit')
    .regex(/^[0-9+\-\s]*$/, 'Format nomor tidak valid')
    // Still optional — but a number that IS typed must be dialable. Counted on
    // digits only, so "+62" and the separators the regex allows do not count.
    .refine((v) => v === '' || v.replace(/\D/g, '').length >= 8, 'Minimal 8 digit')
    .optional()
    .or(z.literal('')),
});

export type CustomerFormValues = z.infer<typeof schema>;

interface CustomerInfoFormProps {
  /** Reports validity upward so the checkout CTA can gate on it. */
  onValidityChange?: (valid: boolean) => void;
}

export function CustomerInfoForm({ onValidityChange }: CustomerInfoFormProps) {
  const { customer, setCustomer } = useCartStore(
    useShallow((s) => ({ customer: s.customer, setCustomer: s.setCustomer })),
  );

  const {
    register,
    formState: { errors, isValid },
    watch,
  } = useForm<CustomerFormValues>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues: { name: customer.name, phone: customer.phone },
  });

  // Mirror form values into the cart store as the operator types.
  useEffect(() => {
    const sub = watch((value) => {
      setCustomer({ name: value.name ?? '', phone: value.phone ?? '' });
    });
    return () => sub.unsubscribe();
  }, [watch, setCustomer]);

  useEffect(() => {
    onValidityChange?.(isValid);
  }, [isValid, onValidityChange]);

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <div className="space-y-1.5">
        <Label htmlFor="cust-name">
          Nama pelanggan <span className="text-destructive">*</span>
        </Label>
        <Input id="cust-name" placeholder="Nama lengkap" {...register('name')} />
        {errors.name && <p className="text-xs font-medium text-destructive">{errors.name.message}</p>}
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="cust-phone">No. telepon (opsional)</Label>
        <Input id="cust-phone" placeholder="08xxxxxxxxxx" inputMode="tel" {...register('phone')} />
        {errors.phone && <p className="text-xs font-medium text-destructive">{errors.phone.message}</p>}
      </div>
    </div>
  );
}
